import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { mapToOptions, Option } from "@sinch/utils/option";
import { isNil } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { Ref, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProfessionsData } from "./queries.graphql";
import { ProfessionsDataQuery } from "./queries.types";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { AutocompleteInputProps, AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

interface ProfessionSelectProps
  extends Pick<AutocompleteInputProps<AutocompleteOptions, false>, "slotProps" | "textFieldProps"> {
  /**
   * Input name
   */
  name: string;
  label: string;
  required?: boolean;
  value: string;
  onChange?: (val: string | number | null) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage: string[];
  inputRef: Ref<any>;
  onBlur: () => void;
}

export const ProfessionSelect: React.FC<ProfessionSelectProps> = (props) => {
  const { t } = useTranslation();
  const { data, loading } = useQueryHandler<ProfessionsDataQuery>(ProfessionsData);

  const professionOptions: Option[] = useMemo(() => mapToOptions("id", "name")(data?.professions || []), [data]);

  useEffect(() => {
    if (isNotNil(data?.professions?.[0]?.id) && props.value === null) {
      props.onChange?.(data?.professions?.[0]?.id);
    }
  }, [data, props.value]);

  return (
    <AutocompleteInput
      {...props}
      filterSelectedOptions
      includeInputInList
      label={t("ShiftTimeline.professions")}
      loading={loading}
      onChange={props.onChange}
      openOnFocus
      options={professionOptions}
      size="small"
      skeleton={!isNil(props.value) && loading}
      value={props.value as string | number}
    />
  );
};
